import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { initGA4 } from "@/lib/analytics";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useKeyboardNavigation, useFocusVisible } from "@/hooks/useKeyboardNavigation";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ChatWidget from "@/components/ChatWidget";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTransition, PageLoadingFallback } from "@/components/PageTransition";
import { ScrollToTop } from "@/components/ScrollToTop";
import { inject } from "@vercel/analytics";
import { injectSpeedInsights } from "@vercel/speed-insights";
import { HelmetProvider } from 'react-helmet-async';

// Lazy load route components for better performance
const Index = lazy(() => import("./pages/Index"));
const Admissions = lazy(() => import("./pages/Admissions"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const Services = lazy(() => import("./pages/Services"));
const Treatment = lazy(() => import("./pages/Treatment"));
const Outpatient = lazy(() => import("./pages/Outpatient"));
const IOP = lazy(() => import("./pages/IOP"));
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const Contact = lazy(() => import("./pages/Contact"));
const InsuranceVerification = lazy(() => import("./pages/InsuranceVerification"));
const InsurancePartners = lazy(() => import("./pages/InsurancePartners"));
const InsuranceHub = lazy(() => import("./pages/InsuranceHub"));
const RecoveryHousing = lazy(() => import("./pages/RecoveryHousing"));
const RecoveryHousingLevel2 = lazy(() => import("./pages/RecoveryHousingLevel2"));
const RecoveryHousingLevel3 = lazy(() => import("./pages/RecoveryHousingLevel3"));
const Team = lazy(() => import("./pages/Team"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const CommunityPartners = lazy(() => import("./pages/CommunityPartners"));
const AcademicPartnerships = lazy(() => import("./pages/AcademicPartnerships"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const NotFound = lazy(() => import("./pages/NotFound"));

const Careers = lazy(() => import("./pages/Careers"));

// Alumni Portal pages
const AlumniLogin = lazy(() => import("./pages/AlumniLogin"));
const AlumniDashboard = lazy(() => import("./pages/AlumniDashboard"));
const AlumniProfile = lazy(() => import("./pages/AlumniProfile"));
const AlumniStories = lazy(() => import("./pages/AlumniStories"));
const AlumniEvents = lazy(() => import("./pages/AlumniEvents"));
const AlumniSupportGroups = lazy(() => import("./pages/AlumniSupportGroups"));
const AlumniFindMentor = lazy(() => import("./pages/AlumniFindMentor"));
const AlumniMilestones = lazy(() => import("./pages/AlumniMilestones"));
const AlumniStoryForm = lazy(() => import("./pages/AlumniStoryForm"));
const AlumniEventDetail = lazy(() => import("./pages/AlumniEventDetail"));
const AlumniCelebrations = lazy(() => import("./pages/AlumniCelebrations"));
const AlumniAdmin = lazy(() => import("./pages/AlumniAdmin"));
const AlumniMessages = lazy(() => import("./pages/AlumniMessages"));
const AlumniDirectory = lazy(() => import("./pages/AlumniDirectory"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const SignatureGenerator = lazy(() => import("./pages/SignatureGenerator"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - cache persists for 10 minutes
      refetchOnWindowFocus: false, // Prevent refetch on window focus for better UX
      retry: 1, // Retry failed requests once
    },
  },
});

// Component to initialize GA4 and track page views
const AppContent = () => {
  usePageTracking();
  useKeyboardNavigation();
  useFocusVisible();
  useSmoothScroll(); // Enable smooth scroll with section anchoring
  const location = useLocation();

  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <PageTransition key={location.pathname}>
        <Routes location={location}>
        <Route path="/" element={<Index />} />
        <Route path="/admissions" element={<Admissions />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/services" element={<Services />} />
        <Route path="/programs/iop" element={<IOP />} />
        <Route path="/recovery-housing" element={<RecoveryHousing />} />
        <Route path="/recovery-housing/level-2" element={<RecoveryHousingLevel2 />} />
        <Route path="/recovery-housing/level-3" element={<RecoveryHousingLevel3 />} />
        <Route path="/about/approach" element={<Treatment />} />
        <Route path="/outpatient" element={<Outpatient />} />
        <Route path="/about" element={<About />} />
        <Route path="/about/team" element={<Team />} />
        <Route path="/about/careers" element={<Careers />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about/partnerships" element={<CommunityPartners />} />
        <Route path="/about/academic-partnerships" element={<AcademicPartnerships />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/insurance-hub" element={<InsuranceHub />} />
        <Route path="/insurance-verification" element={<InsuranceVerification />} />
        <Route path="/insurance" element={<InsurancePartners />} />

        {/* Internal Tools */}
        <Route path="/internal/signature-generator" element={<SignatureGenerator />} />

        {/* Alumni Portal Routes */}
        <Route path="/alumni/login" element={<AlumniLogin />} />
        <Route
          path="/alumni/dashboard"
          element={
            <ProtectedRoute>
              <AlumniDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/profile"
          element={
            <ProtectedRoute>
              <AlumniProfile />
            </ProtectedRoute>
          }
        />
        <Route path="/alumni/stories" element={<AlumniStories />} />
        <Route path="/alumni/events" element={<AlumniEvents />} />
        <Route path="/alumni/support-groups" element={<AlumniSupportGroups />} />
        <Route
          path="/alumni/find-mentor"
          element={
            <ProtectedRoute>
              <AlumniFindMentor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/directory"
          element={
            <ProtectedRoute>
              <AlumniDirectory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/milestones"
          element={
            <ProtectedRoute>
              <AlumniMilestones />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/stories/new"
          element={
            <ProtectedRoute>
              <AlumniStoryForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/events/:id"
          element={
            <ProtectedRoute>
              <AlumniEventDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/celebrations"
          element={
            <ProtectedRoute>
              <AlumniCelebrations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/messages"
          element={
            <ProtectedRoute>
              <AlumniMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alumni/admin"
          element={
            <ProtectedRoute>
              <AlumniAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/chat"
          element={
            <ProtectedRoute>
              <AdminChat />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </PageTransition>
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    // Initialize Google Analytics 4
    initGA4();
    // Initialize Vercel Analytics for Vite/SPA
    inject();
    injectSpeedInsights();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <ScrollToTop />
              <AuthProvider>
                <AppContent />
                {/* <ChatWidget /> */}
                {/* Temporarily disabled - will re-enable when ready */}
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
